/**
 * DocumentIntakeWorkflow.jsx
 * 
 * Main workflow component for the Document Intake Agent.
 * Provides universal document upload and processing interface.
 * 
 * Features:
 * - Universal document upload (any file type)
 * - Multi-tenant support (auditor_id, engagement_id)
 * - Real-time processing status tracking
 * - Structured data visualization
 */

import React, { useState, useEffect } from 'react';
import UniversalUpload from './UniversalUpload';
import ProcessingStatus from './ProcessingStatus';
import StructuredDataViewer from './StructuredDataViewer';
import './DocumentIntakeWorkflow.css';

const DocumentIntakeWorkflow = ({ auditorId, engagementId, onBack }) => {
  const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'processing', 'results'
  const [processingJob, setProcessingJob] = useState(null);
  const [processedDocuments, setProcessedDocuments] = useState([]);
  const [error, setError] = useState(null);

  // Load existing documents on component mount
  useEffect(() => {
    loadExistingDocuments();
  }, [auditorId, engagementId]);

  const loadExistingDocuments = async () => {
    try {
      const response = await fetch(
        `/api/v1/document-intake/documents?auditor_id=${auditorId}&engagement_id=${engagementId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setProcessedDocuments(data.documents || []);
        
        // If documents exist, show results view
        if (data.documents && data.documents.length > 0) {
          setCurrentStep('results');
        }
      }
    } catch (error) {
      console.error('Error loading existing documents:', error);
    }
  };

  const handleUploadComplete = (jobData) => {
    setProcessingJob(jobData);
    setCurrentStep('processing');
    setError(null);
  };

  const handleUploadError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleProcessingComplete = (documents) => {
    setProcessedDocuments(documents);
    setCurrentStep('results');
    setProcessingJob(null);
  };

  const handleProcessingError = (errorMessage) => {
    setError(errorMessage);
    setCurrentStep('upload');
    setProcessingJob(null);
  };

  const handleNewUpload = () => {
    setCurrentStep('upload');
    setProcessingJob(null);
    setError(null);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <UniversalUpload
            auditorId={auditorId}
            engagementId={engagementId}
            onUploadComplete={handleUploadComplete}
            onError={handleUploadError}
          />
        );
      
      case 'processing':
        return (
          <ProcessingStatus
            auditorId={auditorId}
            engagementId={engagementId}
            jobId={processingJob?.job_id}
            onComplete={handleProcessingComplete}
            onError={handleProcessingError}
          />
        );
      
      case 'results':
        return (
          <StructuredDataViewer
            auditorId={auditorId}
            engagementId={engagementId}
            documents={processedDocuments}
            onNewUpload={handleNewUpload}
          />
        );
      
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <div className="document-intake-workflow">
      {/* Header */}
      <div className="workflow-header">
        <div className="header-content">
          <button className="back-button" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <div className="header-info">
            <h1>Document Intake Agent</h1>
            <p>Universal document processing and structure extraction</p>
            <div className="engagement-info">
              <span>Auditor: {auditorId}</span>
              <span>Engagement: {engagementId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="progress-indicator">
        <div className={`step ${currentStep === 'upload' ? 'active' : currentStep !== 'upload' ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Upload Documents</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${currentStep === 'processing' ? 'active' : currentStep === 'results' ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Processing</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${currentStep === 'results' ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Results</div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <span className="error-message">{error}</span>
            <button className="error-close" onClick={() => setError(null)}>×</button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="workflow-content">
        {renderCurrentStep()}
      </div>

      {/* Footer */}
      <div className="workflow-footer">
        <div className="footer-info">
          <span>Document Intake Agent v1.0</span>
          <span>Universal document processing for audit workflows</span>
        </div>
      </div>
    </div>
  );
};

export default DocumentIntakeWorkflow;
